import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import packageJson from '../../../../package.json';

export type UiVersion = 'v2' | 'v3';

const VERSION_KEY = 'kanithan-ui-version';
const BANNER_KEY = 'kanithan-ui-banner-dismissed';
/** Dismissing the banner lasts for this app version only (older builds stored `'true'`). */
const APP_VERSION = packageJson.version;

/**
 * Sub-paths that exist in both the v2 and the v3 shell. A path that matches one of these
 * maps 1:1 between /v2/<path> and /v3/<path>; anything else goes to the shell root.
 */
const SHARED_PATHS: RegExp[] = [
  /^dashboard$/,
  /^chat$/,
  /^trips$/,
  /^trips\/[^/]+$/,
  /^trips\/[^/]+\/report$/,
  /^reports$/,
  /^settings$/,
  /^habits$/,
  /^notifications$/,
];

/** Strips query string, fragment and the leading `/v2/` or `/v3/` segment from a URL. */
export function shellSubPath(url: string): string {
  const path = url.split(/[?#]/)[0];
  return path.replace(/^\/?v[23](\/|$)/, '').replace(/\/+$/, '');
}

export function hasSharedEquivalent(subPath: string): boolean {
  return SHARED_PATHS.some((re) => re.test(subPath));
}

/** Returns the equivalent URL in the given shell for a URL from the other shell. */
export function equivalentUrl(url: string, target: UiVersion): string {
  const sub = shellSubPath(url);
  return sub && hasSharedEquivalent(sub) ? `/${target}/${sub}` : `/${target}`;
}

@Injectable({ providedIn: 'root' })
export class UiVersionService {
  private readonly router = inject(Router);

  readonly version = signal<UiVersion>(readVersion());
  readonly bannerDismissed = signal<boolean>(localStorage.getItem(BANNER_KEY) === APP_VERSION);

  /** Saves the preference, re-shows the switch banner and navigates to the same screen in the other shell. */
  switchTo(version: UiVersion): void {
    this.setVersion(version);
    this.setBannerDismissed(false);
    void this.router.navigateByUrl(equivalentUrl(this.router.url, version));
  }

  setVersion(version: UiVersion): void {
    this.version.set(version);
    localStorage.setItem(VERSION_KEY, version);
  }

  dismissBanner(): void {
    this.setBannerDismissed(true);
  }

  private setBannerDismissed(dismissed: boolean): void {
    this.bannerDismissed.set(dismissed);
    if (dismissed) localStorage.setItem(BANNER_KEY, APP_VERSION);
    else localStorage.removeItem(BANNER_KEY);
  }
}

function readVersion(): UiVersion {
  return localStorage.getItem(VERSION_KEY) === 'v3' ? 'v3' : 'v2';
}
